import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { changeExtensionToPdf } from "../../../src/taskpane/components/helpers/stringHelper";
import { SharePointFolderContentTypeIds, validPdfExtensions } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { ConvertAndSaveAsPDFRequest, FilePreCommitRequest } from "../../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { IFilePreCommitResult } from "../../../src/taskpane/interfaces/IFilePreCommitResult";
import { getFilePreCommitInfo } from "../../../src/taskpane/services/SharePointRestServices";
import { GridProps } from "../useContentManagerBody";
import { apiFileConvertAndSaveAsPDF } from "../../../src/taskpane/services/ApiService";
import { getOneDriveRootDetails } from "../../../src/taskpane/services/GraphService";
import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { DocumentPdf20Filled } from "../../../src/taskpane/components/_v2/components/SvgIcons/DocumentPdf20Filled";
import { resolveDestinationUrl } from "../../../src/taskpane/components/helpers/urlDestination";

export function useMenuItem_SaveAsPDF(
  selectedItems: any[],
  waitFor: boolean,
  setWaitFor: (waitFor: boolean) => void,
  isInOneDrive: boolean,
  setIsCMErrorModalOpen: (isCMErrorModalOpen: boolean) => void,
  setErrorMessage?: (errorMessage: string) => void,
  openDuplicateFileVersionSelectorModal?: (isDuplicate: boolean, showVersion: boolean) => void,
  reloadGrid?: (gridData?: GridProps) => void) {
  const languageStrings = React.useContext(LanguageContext);

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const tenantId = useSelector((state: RootState) => state.user?.account?.tenantId);
  const accountId = useSelector((state: RootState) => state.user?.account?.accountId);

  const initiateSaveAndConvertAsPdfFile = React.useCallback((selectedItem: any) => {
    (async () => {
      let keepWaitFor = false;
      setWaitFor(true);

      try {
        let driveId: string;
        let siteUrl: string;
        let sharePointToken: string;

        if (isInOneDrive || currentDirectory.webUrl?.includes("sharepoint.com/personal/")) {
          let oneDriveDetails: IOneDriveRootDetails = oneDriveRootDetails;
          if (!oneDriveDetails) {
            oneDriveDetails = await getOneDriveRootDetails();
          }

          driveId = oneDriveDetails.driveId;
          siteUrl = oneDriveDetails.sharePointSiteUrl;
          sharePointToken = await authModule.getOneDriveAccessToken();
        }
        else {
          driveId = !currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId;
          siteUrl = currentDirectory.webUrl;
          sharePointToken = await authModule.getSharePointAccessToken();
        }

        const spItemUrlparts = selectedItem['.spItemUrl'].split('/');
        let itemId: string = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

        let pdfFileName = changeExtensionToPdf(selectedItem?.FileLeafRef);

        const destinationUrl = await resolveDestinationUrl(currentDirectory, { isOneDrive: isInOneDrive });

        const fileRequest: FilePreCommitRequest = {
          accessToken: sharePointToken,
          destinationUrl,
          siteUrl,
          fileName: pdfFileName,
          autoFolderCreation: false
        }

        const preCommitResult: IFilePreCommitResult = await getFilePreCommitInfo(fileRequest);

        let saveAndConvert = true;
        let isDuplicate = false;
        if (!preCommitResult || preCommitResult.exists === true) {
          isDuplicate = true;
          saveAndConvert = false;
        }

        if (currentSharepointFolder?.enableMinorVersions === true || isDuplicate) {
          openDuplicateFileVersionSelectorModal?.(isDuplicate, currentSharepointFolder?.enableMinorVersions === true);
          saveAndConvert = false;
          keepWaitFor = true;
        }

        if (saveAndConvert) {
          const request: ConvertAndSaveAsPDFRequest = {
            graphToken: await authModule.getGraphAccessToken(),
            sharePointToken,
            tenantGuid: tenantId,
            accountId,
            driveId,
            itemId,
            siteUrl,
            listId: currentDirectory.sharePointListId,
            teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
            channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId,
            fileUniqueId: selectedItem?.UniqueId.replace("{", "").replace("}", ""),
            EnsureUniqueFilename: false,
            checkinType: null,
          }
          try {
            await apiFileConvertAndSaveAsPDF(request);
            reloadGrid?.();
          } catch (error) {
            logger(error, "FileOptionsPanel.tsx (saveAsPdfFile)");
          }
        }
      } catch (error) {
        logger(error, "useMenuItem_SaveAsPDF.tsx (initiateSaveAndConvertAsPdfFile)");
      } finally {
        if (!keepWaitFor) {
          setWaitFor(false);
        }
      }
    })();
  }, [setWaitFor, isInOneDrive, currentDirectory, oneDriveRootDetails, currentSharepointFolder, openDuplicateFileVersionSelectorModal, tenantId, accountId, workingDirectories, reloadGrid]);

  const menuItem_SaveAsPDF = React.useMemo(() => {
    const hasSingleSelection = selectedItems?.length === 1;
    if (!hasSingleSelection) {
      return null;
    }

    const selectedItem = selectedItems[0];

    const canOpenFile = selectedItem.EffectiveBasePermissions?.canOpenItems;
    const isNotFolder = !selectedItem.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder);
    const isValidPdfExtension = validPdfExtensions.some(validExt => selectedItem[".fileType"]?.replace(".", "")?.toLowerCase() === validExt);

    if (canOpenFile && isNotFolder && isValidPdfExtension) {
      return {
        key: "saveAsPDF",
        disabled: (waitFor === true || isBusySaving === true),
        text: languageStrings.SaveAsPdf,
        // iconProps: { iconName: "PDF" },
        iconProps: {},
        onRenderIcon: () => <DocumentPdf20Filled className={menuItemIconStyles} />,
        onClick: () => {
          initiateSaveAndConvertAsPdfFile(selectedItem);
        }
      } as IContextualMenuItem;
    }

    return null;
  }, [selectedItems, waitFor, isBusySaving, languageStrings, initiateSaveAndConvertAsPdfFile]);

  return {
    menuItem_SaveAsPDF
  }
}
